/**
 * FormField - Base wrapper for form fields
 *
 * Provides consistent layout with label, icon support, error display, and hints.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { formStyles } from './formStyles';

export interface FormFieldProps {
  /** Field label */
  label: string;
  /** Mark field as required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Optional icon displayed in the label area */
  icon?: LucideIcon;
  /** Help text displayed below the field */
  hint?: string;
  /** The form input element(s) */
  children: ReactNode;
  /** Custom className for the wrapper */
  className?: string;
}

export function FormField({
  label,
  required,
  error,
  icon: Icon,
  hint,
  children,
  className,
}: FormFieldProps) {
  return (
    <div style={formStyles.field} className={className}>
      <label style={formStyles.fieldLabel}>
        {Icon && (
          <Icon
            size={14}
            style={{ marginRight: '6px', verticalAlign: 'middle', opacity: 0.7 }}
          />
        )}
        {label}
        {required && <span style={formStyles.fieldRequired}>*</span>}
      </label>
      {children}
      {error && <div style={formStyles.fieldError}>{error}</div>}
      {hint && !error && <div style={formStyles.fieldHint}>{hint}</div>}
    </div>
  );
}
